import React from 'react'
import { format } from '@fs/zion-locale/date-fns'
import { useFeatureFlag } from '@fs/zion-flags'
import { useTranslation } from 'react-i18next'
import { Badge } from './Badge'
import { getI18nDate, getNowTimestamp } from './helpers/dateTimeHelpers'
import { day, FLAGS } from '../config'
import { MonoText } from './type'
// import { propTypes as brightspot } from '../api/brightspot'

const sevenDays = 7 * day

export default function Timestamp({
  timestamp,
  conferenceYear,
  isLive = false,
  inline = false,
}: {
  timestamp?: string | null
  conferenceYear?: string | null
  isLive?: boolean
  inline?: boolean
}): JSX.Element | null {
  // short-circuit to simplify the logic
  const scheduleFlag = useFeatureFlag(FLAGS.SCHEDULE)
  const [t] = useTranslation()

  // Clean data from Brightspot - trim whitespace and coerce to a number so we
  // never format a raw/whitespace string into an Invalid Date.
  const cleanTimestamp = timestamp == null ? null : Number(String(timestamp).trim())
  const hasValidTimestamp = cleanTimestamp != null && Number.isFinite(cleanTimestamp)

  /**
   * 1. if it's in the past...
   *   a. don't show the time, timezone, or weekday.
   *   b. show the year
   * 2. if it's in the future and...
   *   a. it's in the next 7 days, show the weekday, time, and timezone.
   *   b. it's further out than the next seven days, show the month, day, year, time, and timezone
   */
  const formatOptions = {
    time: isLive,
    timezone: isLive,
    weekday: isLive && hasValidTimestamp && cleanTimestamp - getNowTimestamp() < sevenDays,
    year: !isLive || (hasValidTimestamp && `${new Date(cleanTimestamp).getFullYear()}` !== conferenceYear),
  }

  const showTimestamp = isLive && hasValidTimestamp && scheduleFlag.isOn // currently running or in the future

  const label = showTimestamp ? (
    <time dateTime={format(cleanTimestamp, 'yyyy-MM-dd h:MM')}>{getI18nDate(cleanTimestamp, formatOptions)}</time>
  ) : (
    conferenceYear
  )
  const testId = showTimestamp ? 'timestamp' : 'year'
  const ariaLabel = showTimestamp
    ? t('badge.timestamp', 'Session is available on {timestamp}', {
        timestamp: getI18nDate(cleanTimestamp, formatOptions),
      })
    : t('badge.year', 'Session was published in {year}', { year: conferenceYear })

  if (!label) return null

  if (inline) {
    return (
      <span data-testid={`item-${testId}`} aria-label={ariaLabel}>
        {label}
      </span>
    )
  }

  return (
    // This div needs to stay so the outside Row can growX if needed
    <div>
      <Badge
        data-testid={`item-${testId}`}
        keyline="left"
        label={<MonoText complementary={false}>{label}</MonoText>}
        aria-label={ariaLabel}
      />
    </div>
  )
}
